/**
 * STT (Speech-to-Text) qua gRPC Viettel TV360 - WebSocket contract mới với chat-service.
 * Endpoint riêng biệt với WS chat thường (`/api/ws/chat`), cùng cơ chế base URL/prefix.
 */

/** API (qua ai-gateway) trả provider STT + thông tin kết nối gRPC cho từng loại thiết bị. */
export const STT_GRPC_TOKEN_URL = '/ai-gateway/api/proxy/chat-service/api/chat/stt/viettel/grpc-token';

/** deviceType gửi kèm API grpc-token: web / android / ios (web dùng giá trị 'web'). */
export const STT_DEVICE_TYPE_WEB = 'web';

/**
 * Model STT do BE quyết định theo app_config của từng thiết bị:
 * - VIETTEL: dùng STT gRPC Viettel (useGrpcSpeechToText).
 * - DEFAULT_SDK: dùng STT mặc định của trình duyệt (useSpeechToText, Web Speech API).
 */
export const STT_PROVIDER = {
  VIETTEL: 'VIETTEL',
  DEFAULT_SDK: 'DEFAULT_SDK',
} as const;

export type SttProvider = (typeof STT_PROVIDER)[keyof typeof STT_PROVIDER];

/** Path của WebSocket STT, nối sau base URL WS chung (xem getChatWebSocketBaseUrl trong chatService.ts). */
export const STT_WS_PATH = '/api/ws/chat/stt';

/** Tên query param khi mở WebSocket STT. */
export const STT_WS_QUERY_PARAM = {
  CONVERSATION_ID: 'conversationId',
  TOKEN: 'token',
} as const;

/** Lệnh điều khiển gửi qua TextMessage tới WS STT. */
export const STT_COMMAND = {
  STOP: 'STOP',
} as const;

/** Các event trạng thái nhận dạng giọng nói server có thể gửi kèm kết quả. */
export const STT_EVENT = {
  SPEECH_ACTIVITY_BEGIN: 'SPEECH_ACTIVITY_BEGIN',
  SPEECH_ACTIVITY_END: 'SPEECH_ACTIVITY_END',
  END_OF_UTTERANCE: 'END_OF_UTTERANCE',
  NO_SPEECH_TIMEOUT: 'NO_SPEECH_TIMEOUT',
  FINAL: 'FINAL',
  /** Lỗi stream ASR - text là thông báo lỗi kỹ thuật, không phải transcript (không hiển thị). */
  ERROR: 'ERROR',
  /** Keepalive từ Viettel - không mang transcript thật, phải bỏ qua (không hiển thị). */
  PING: 'PING',
} as const;

/** Các giá trị `status` server có thể trả về (nguyên văn từ Viettel, không đồng nhất hoa/thường nên so sánh không phân biệt hoa thường). */
export const STT_STATUS = {
  PROCESSING: 'processing',
  PARTIAL: 'partial',
  FINAL: 'final',
  ERROR: 'error',
} as const;

/** Sample rate bắt buộc gửi lên server - đúng kỳ vọng của Viettel STT (PCM16LE 16kHz mono). */
export const STT_SAMPLE_RATE = 16000;

/** AudioWorklet module đặt trong public/ để tách encode PCM16LE khỏi main thread. */
export const PCM_WORKLET_MODULE_URL = '/pcm-worklet.js';
export const PCM_WORKLET_PROCESSOR_NAME = 'pcm-worklet';

/** Độ dài mỗi cụm audio gửi lên server theo contract (~100ms/frame). */
export const PCM_FRAME_DURATION_MS = 100;

/**
 * Sau khi gửi STOP, giữ WS mở tối đa chừng này ms để chờ server trả nốt text (event FINAL /
 * END_OF_UTTERANCE) rồi mới đóng - tránh mất text khi server còn đang xử lý ASR.
 */
export const STT_FINAL_WAIT_MS = 3000;


/** sessionStorage keys tái sử dụng từ cơ chế chat hiện có (xem useChatToken.ts, InputMessage.tsx). */
export const CHAT_SESSION_STORAGE_KEY = {
  ACCESS_TOKEN: 'access_token',
  CONVERSATION_ID: 'current_conversation_id',
} as const;

/** Thông báo lỗi mic tiếng Việt - giữ nguyên y hệt bản cũ (useSpeechToText.ts) cho từng loại lỗi. */
export const MIC_ERROR_MESSAGE = {
  NOT_ALLOWED: 'Bạn chưa có quyền sử dụng micro. Vui lòng bật lại trong browser settings',
  NOT_FOUND: 'Không tìm thấy microphone trên thiết bị',
  NOT_READABLE: 'Không thể kích hoạt micro khi một ứng dụng khác đang dùng micro',
  GENERIC: 'Không thể kích hoạt micro',
  UNSUPPORTED: 'Trình duyệt không hỗ trợ ghi âm',
  MISSING_SESSION: 'Không thể kết nối tới máy chủ nhận dạng giọng nói',
} as const;
